import {
  PAYMENT_STATUS_LABELS,
  formatDateShort,
  formatMoney,
  navigationUrl,
  plural,
  telHref,
  type ClientDetailResponse,
  type ClientObjectDto,
} from '@kominiarz/shared';
import { CalendarPlus, Clock, Mail, MapPin, Navigation, Pencil, Phone, Plus, StickyNote, Trash2, UserX } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useCurrentUser } from '../../auth/useAuth';
import { Alert } from '../../components/Alert';
import { ActionTile, ActionTiles } from '../../components/ActionTiles';
import { Button, ButtonAnchor, ButtonLink } from '../../components/Button';
import { ColorDot } from '../../components/ColorDot';
import { useConfirm } from '../../components/Confirm';
import { Spinner } from '../../components/Loading';
import { ActionRow, InfoRow, List } from '../../components/List';
import { PageBody } from '../../components/PageBody';
import { PageHeader } from '../../components/PageHeader';
import { Section } from '../../components/Section';
import { useToast } from '../../components/Toast';
import { UnpaidBadge, VisitStatusBadge } from '../../components/VisitStatus';
import { errorMessage } from '../../lib/api';
import { local } from '../../lib/calendarDates';
import { useAnonymizeClient, useClient, useRemoveObject } from '../../lib/clientsApi';
import { AuditList } from '../visits/AuditList';
import { ObjectDialog } from './ObjectDialog';
import styles from './clients.module.css';

export function ClientDetailPage() {
  const id = Number(useParams().id);
  const query = useClient(Number.isFinite(id) ? id : null);
  if (query.isPending || query.isError) {
    return (
      <>
        <PageHeader title="Klient" back="/klienci" />
        <PageBody>{query.isPending ? <Spinner /> : <Alert kind="error">{errorMessage(query.error)}</Alert>}</PageBody>
      </>
    );
  }
  return <ClientCard data={query.data} />;
}

function ClientCard({ data }: { data: ClientDetailResponse }) {
  const { client: c, visits, audit } = data;
  const user = useCurrentUser();
  const confirm = useConfirm();
  const toast = useToast();
  const anonymize = useAnonymizeClient(c.id);
  const removeObject = useRemoveObject();
  const [objectDialog, setObjectDialog] = useState<ClientObjectDto | 'new' | null>(null);

  const upcoming = visits.filter((v) => v.status === 'planned' && new Date(v.startAt) > new Date()).length;
  const lastDone = visits.find((v) => v.status === 'done');

  async function onAnonymize() {
    const ok = await confirm({
      title: 'Usunąć dane osobowe klienta?',
      message: (
        <>
          Zostaną usunięte: imię i nazwisko, telefony, e-mail, notatki, adresy i opisy wizyt. Zostaną daty, usługi,
          kwoty i statusy wizyt (z dopiskiem „klient usunięty”).
          {upcoming > 0 && ` ${plural(upcoming, 'zaplanowana wizyta zostanie odwołana', 'zaplanowane wizyty zostaną odwołane', 'zaplanowanych wizyt zostanie odwołanych')}.`}{' '}
          Tego nie da się cofnąć.
        </>
      ),
      confirmLabel: 'Usuń dane osobowe',
      cancelLabel: 'Nie usuwaj',
      danger: true,
    });
    if (!ok) return;
    try {
      await anonymize.mutateAsync();
      toast.success('Dane osobowe klienta usunięte');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  async function onRemoveObject(o: ClientObjectDto) {
    const ok = await confirm({
      title: 'Usunąć adres?',
      message: `${o.address}. Wizyty pod tym adresem zostaną w historii.`,
      confirmLabel: 'Usuń adres',
      danger: true,
    });
    if (!ok) return;
    try {
      await removeObject.mutateAsync(o.id);
      toast.success('Adres usunięty');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  if (c.anonymizedAt) {
    return (
      <>
        <PageHeader title="Klient usunięty" back="/klienci" />
        <PageBody>
          <Alert kind="info">
            Dane osobowe usunięto {formatDateShort(c.anonymizedAt)}. W historii zostały daty, usługi i kwoty wizyt.
          </Alert>
          <VisitHistory visits={visits} />
          {audit && audit.length > 0 && (
            <Section title="Historia zmian">
              <AuditList entries={audit} />
            </Section>
          )}
        </PageBody>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={c.name ?? c.phones[0]?.phone ?? 'Klient'}
        subtitle={visits.length ? `${plural(visits.length, 'wizyta', 'wizyty', 'wizyt')}${lastDone ? `, ostatnia wykonana ${formatDateShort(lastDone.startAt)}` : ''}` : 'Jeszcze bez wizyt'}
        back="/klienci"
      />
      <PageBody>
        <div className={styles.columns}>
          <div className={styles.column}>
            <ActionTiles>
              {c.phones[0] && <ActionTile href={telHref(c.phones[0].phone)} icon={<Phone aria-hidden="true" />} label="Zadzwoń" />}
              <ActionTile primary to={`/wizyty/nowa?klient=${c.id}`} icon={<CalendarPlus aria-hidden="true" />} label="Nowa wizyta" />
            </ActionTiles>

            <Section title="Kontakt" flush>
              <List>
                {c.phones.map((p, i) => (
                  <InfoRow key={p.id} icon={Phone} label={p.label ?? (c.phones.length > 1 ? `Telefon ${i + 1}` : 'Telefon')}>
                    <a href={telHref(p.phone)}>{p.phone}</a>
                  </InfoRow>
                ))}
                {c.email && (
                  <InfoRow icon={Mail} label="E-mail">
                    <a href={`mailto:${c.email}`}>{c.email}</a>
                  </InfoRow>
                )}
                {c.note && (
                  <InfoRow icon={StickyNote} label="Notatka">
                    {c.note}
                  </InfoRow>
                )}
                <InfoRow icon={Clock} label="Dodano">
                  {formatDateShort(c.createdAt)} ({c.createdBy.name})
                </InfoRow>
              </List>
              {c.permissions.edit && (
                <List divided>
                  <ActionRow icon={Pencil} to={`/klienci/${c.id}/edycja`}>
                    Edytuj dane
                  </ActionRow>
                </List>
              )}
            </Section>
          </div>

          <Section title={c.objects.length > 1 ? 'Adresy' : 'Adres'} flush>
            {c.objects.length === 0 && <p className={styles.emptyRow}>Brak zapisanych adresów.</p>}
            {c.objects.length > 0 && (
              <ul className={styles.objects}>
                {c.objects.map((o) => {
                  const nav = navigationUrl(user.settings.navApp, o);
                  return (
                    <li key={o.id} className={styles.object}>
                      <div className={styles.objectHead}>
                        <MapPin aria-hidden="true" />
                        <div className={styles.objectText}>
                          {o.label && <span className={styles.objectLabel}>{o.label}</span>}
                          <span className={styles.objectAddress}>{o.address}</span>
                          {o.note && <span className={styles.objectNote}>{o.note}</span>}
                          {o.nextInspectionDue && (
                            <span className={styles.objectDue}>Następny przegląd: {formatDateShort(o.nextInspectionDue)}</span>
                          )}
                        </div>
                      </div>
                      <div className={styles.objectButtons}>
                        <ButtonLink
                          to={`/wizyty/nowa?klient=${c.id}&obiekt=${o.id}`}
                          size="sm"
                          icon={<CalendarPlus aria-hidden="true" />}
                        >
                          Wizyta tutaj
                        </ButtonLink>
                        {nav && (
                          <ButtonAnchor href={nav} target="_blank" rel="noopener noreferrer" size="sm" icon={<Navigation aria-hidden="true" />}>
                            Nawiguj
                          </ButtonAnchor>
                        )}
                        {c.permissions.edit && (
                          <>
                            <Button variant="ghost" size="sm" icon={<Pencil aria-hidden="true" />} onClick={() => setObjectDialog(o)}>
                              Zmień
                            </Button>
                            <Button variant="ghost" size="sm" icon={<Trash2 aria-hidden="true" />} onClick={() => onRemoveObject(o)}>
                              Usuń
                            </Button>
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            <List divided>
              <ActionRow icon={Plus} onClick={() => setObjectDialog('new')}>
                Dodaj adres
              </ActionRow>
            </List>
          </Section>
        </div>

        <VisitHistory visits={visits} />

        {audit && audit.length > 0 && (
          <Section title="Historia zmian">
            <AuditList entries={audit} />
          </Section>
        )}

        {c.permissions.anonymize && (
          <Section
            title="Dane osobowe (RODO)"
            description="Na żądanie klienta można usunąć jego dane osobowe. Wizyty zostaną w rozliczeniach bez danych osobowych."
            flush
          >
            <List>
              <ActionRow icon={UserX} tone="danger" onClick={onAnonymize} disabled={anonymize.isPending}>
                Usuń dane osobowe
              </ActionRow>
            </List>
          </Section>
        )}
      </PageBody>
      {objectDialog && (
        <ObjectDialog clientId={c.id} object={objectDialog === 'new' ? null : objectDialog} onClose={() => setObjectDialog(null)} />
      )}
    </>
  );
}

function VisitHistory({ visits }: { visits: ClientDetailResponse['visits'] }) {
  return (
    <Section title="Historia wizyt" flush>
      {visits.length === 0 ? (
        <p className={styles.emptyRow}>Brak wizyt.</p>
      ) : (
        <ul className={styles.history}>
          {visits.map((v) => (
            <li key={v.id}>
              <Link to={`/wizyty/${v.id}`} className={styles.historyLink}>
                <span className={styles.historyTop}>
                  {local(v.startAt).toFormat('dd.LL.yyyy, HH:mm')}
                  <span>{v.serviceType ?? 'Wizyta'}</span>
                  <VisitStatusBadge status={v.status} />
                  <UnpaidBadge visit={v} />
                </span>
                <span className={styles.historyMeta}>
                  {v.address && (
                    <span>
                      <MapPin aria-hidden="true" width="1em" height="1em" style={{ display: 'inline', verticalAlign: '-0.1em' }} />{' '}
                      {v.address}
                    </span>
                  )}
                  {v.amountGrosze != null && (
                    <span>
                      {formatMoney(v.amountGrosze)}, {PAYMENT_STATUS_LABELS[v.paymentStatus].toLowerCase()}
                    </span>
                  )}
                  <span>
                    <ColorDot color={v.assignee.color} size="sm" /> {v.assignee.name}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
